import { useSelector } from "react-redux";
import { isEmpty } from "lodash";

import { isDataInBrief } from "../utils";
import { RootState } from "src/redux/reducers";
import {
  ProjectSetupType,
  type SelectOption,
  type ProjectAction,
} from "../types";

interface UseProjectValidationProps {
  projectAction: ProjectAction;
  projectName: string;
  selectedCurrencyOption: string | null;
  selectedBaseTemplateOption: SelectOption | null;
  selectedExistingProjectOption: SelectOption | null;
  selectedSocialPlatformTemplateOptions: SelectOption[];
  coverImageFile: File | null;
  currentProjectSetupOption: ProjectSetupType;
}

export default function useProjectValidation({
  projectAction,
  projectName,
  selectedCurrencyOption,
  selectedBaseTemplateOption,
  selectedExistingProjectOption,
  selectedSocialPlatformTemplateOptions,
  coverImageFile,
  currentProjectSetupOption,
}: UseProjectValidationProps) {
  const { newProjectBriefData } = useSelector(
    (state: RootState) => state.mainReducer,
  );

  const { globalMetaFieldProjectWizardFields } = useSelector(
    (state: RootState) => state.projectReducer,
  );

  const areRequiredFieldsEmpty = () => {
    switch (currentProjectSetupOption) {
      case ProjectSetupType.Blank:
        return !selectedCurrencyOption;
      case ProjectSetupType.Template: {
        const doRequiredFieldsHaveValue =
          globalMetaFieldProjectWizardFields.every((field) => {
            if (field.valueRequired) {
              return !isEmpty(field.defaultValue) || !isEmpty(field.value);
            }
            return true;
          });
        return (
          !selectedBaseTemplateOption?.value ||
          !selectedCurrencyOption ||
          !doRequiredFieldsHaveValue
        );
      }
      case ProjectSetupType.BasedOnExistingProject:
        return !selectedExistingProjectOption;
      default:
        const _exhaustiveCheck: never = currentProjectSetupOption;
        return _exhaustiveCheck;
    }
  };

  const createCampaignButtonDisabled =
    !projectName.length || areRequiredFieldsEmpty();

  let hasUnsavedChanges =
    projectName.length > 0 ||
    selectedCurrencyOption !== null ||
    selectedBaseTemplateOption !== null ||
    selectedExistingProjectOption !== null ||
    selectedSocialPlatformTemplateOptions.length > 0 ||
    coverImageFile !== null;

  if (projectAction === "create" && isDataInBrief(newProjectBriefData)) {
    hasUnsavedChanges = true;
  }

  return { hasUnsavedChanges, createCampaignButtonDisabled };
}
